import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@hyperhedral/ui";
import { useAuthStore } from "../../stores/auth";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const { signIn, resetPassword, loading } = useAuthStore();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const { error } = await signIn(email, password);
    if (error) {
      setError(error);
    } else {
      navigate(search.get("next") === "welcome" ? "/welcome" : "/portal/dashboard", { replace: true });
    }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const { error } = await resetPassword(email);
    if (error) {
      setError(error);
    } else {
      setResetSent(true);
    }
  }

  return (
    <div className="account-page flex min-h-[calc(100vh-5rem)] items-center justify-center px-6 py-16">
      <div className="account-card w-full max-w-md">
        <div className="text-center">
          <img
            src="/android-chrome-192x192.png"
            alt="Bookburrow"
            className="mx-auto h-14 w-14 rounded-2xl shadow-lg"
          />
          <h1 className="mt-6 font-display text-2xl font-semibold text-forest-950">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-bark-600">
            Sign in to your school portal.
          </p>
        </div>

        {error && (
          <div className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {showReset ? (
          <form onSubmit={handleReset} className="mt-10 space-y-5">
            {resetSent ? (
              <div className="rounded-lg bg-forest-50 px-4 py-3 text-sm text-forest-700">
                Check your email for a password reset link.
              </div>
            ) : (
              <>
                <Input
                  label="Email address"
                  type="email"
                  placeholder="you@school.sch.uk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-2 w-full rounded-xl bg-forest-800 px-4 py-3 text-sm font-semibold text-cream-50 transition-colors hover:bg-forest-700 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send reset link"}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => {
                setShowReset(false);
                setResetSent(false);
                setError(null);
              }}
              className="w-full text-center text-xs text-forest-600 underline decoration-forest-300 underline-offset-2"
            >
              Back to sign in
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="mt-10 space-y-5">
            <Input
              label="Email address"
              type="email"
              placeholder="you@school.sch.uk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              label="Password"
              type="password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setShowReset(true);
                  setError(null);
                }}
                className="text-xs text-bark-600/60 underline decoration-bark-400/40 underline-offset-2 hover:text-bark-700"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-2 w-full rounded-xl bg-forest-800 px-4 py-3 text-sm font-semibold text-cream-50 transition-colors hover:bg-forest-700 disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-xs text-bark-600/60">
          Don't have an account?{" "}
          <a
            href="/#waitlist"
            className="font-medium text-forest-600 underline decoration-forest-300 underline-offset-2"
          >
            Join the waitlist
          </a>
        </p>
      </div>
    </div>
  );
}
